import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardList, FileText, ListChecks, Loader2, Mic, Pause, Play, Square, Upload, X } from "lucide-react";

const SEGMENT_MS = 10 * 60 * 1000;
export const DOC_TYPES = { ATA: "Ata", RELATORIO: "Relatório", PLANO_ACAO: "Plano de Ação", PLANO_REFORCO: "Plano de Reforço e Incentivo" };
const CHOOSABLE = ["ATA", "RELATORIO", "PLANO_ACAO"];
export const KINDS = { REUNIAO: "Reunião", ATENDIMENTO: "Atendimento" };
const DOC_ICONS = { ATA: FileText, RELATORIO: ClipboardList, PLANO_ACAO: ListChecks };
const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const waitStatus = async (id, done) => {
  for (let i = 0; i < 400; i++) {
    const { data } = await api.get(`/meetings/${id}`);
    if (done.includes(data.status)) return data;
    if (data.status?.startsWith("ERRO")) throw new Error(data.error || "Falha no processamento");
    await new Promise((r) => setTimeout(r, 3000));
  }
  throw new Error("Tempo esgotado. Abra o registro em Reuniões e Atendimentos.");
};

export const StudentPicker = ({ value, onChange }) => {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (q.length < 2) { setOpts([]); return undefined; }
    const t = setTimeout(() => api.get("/students", { params: { q } }).then((r) => setOpts((r.data.items || r.data).slice(0, 8))), 300);
    return () => clearTimeout(t);
  }, [q]);
  if (value) return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="meeting-student-selected">
      <span className="font-medium text-slate-700">{value.full_name}</span>
      <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-red-600" data-testid="meeting-student-clear"><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar estudante (opcional)" data-testid="meeting-student-search" />
      {opts.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-white shadow-lg">
          {opts.map((s) => (
            <button key={s.id} type="button" onClick={() => { onChange({ id: s.id, full_name: s.full_name }); setQ(""); }}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`meeting-student-opt-${s.id}`}>
              {s.full_name} <span className="text-xs text-slate-400">{s.class_name || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ChooseDocDialog = ({ meeting, open, onClose, onDone }) => {
  const [docType, setDocType] = useState("ATA");
  const [kind, setKind] = useState("REUNIAO");
  const [title, setTitle] = useState("");
  const [student, setStudent] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!meeting) return;
    setKind(meeting.kind || "REUNIAO"); setTitle(meeting.title || "");
    setStudent(meeting.student_id ? { id: meeting.student_id, full_name: meeting.student_name } : null);
    setDocType(CHOOSABLE.includes(meeting.doc_type) ? meeting.doc_type : (meeting.kind === "ATENDIMENTO" ? "RELATORIO" : "ATA"));
  }, [meeting]);
  const go = async () => {
    if (meeting.content && !window.confirm("O texto atual do documento será substituído pelo novo modelo gerado pela IA. Continuar?")) return;
    setBusy(true);
    try {
      await api.post(`/meetings/${meeting.id}/generate`, { doc_type: docType, kind, title, student_id: student?.id || null });
      const m = await waitStatus(meeting.id, ["PRONTO"]);
      toast.success(`${DOC_TYPES[docType]} gerado(a). Revise e edite o texto.`);
      onDone(m);
    } catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="choose-doc-dialog">
        <DialogHeader><DialogTitle className="pr-6">Gravação transcrita — qual documento deseja registrar?</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {CHOOSABLE.map((k) => [k, DOC_TYPES[k]]).map(([k, v]) => {
            const I = DOC_ICONS[k];
            return (
              <button key={k} type="button" onClick={() => setDocType(k)} data-testid={`doc-type-${k}`}
                className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-sm font-semibold transition-colors ${docType === k ? "border-blue-700 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                <I className="h-6 w-6" />{v}
              </button>
            );
          })}
        </div>
        <div className="grid gap-3">
          <div className="flex gap-2">
            {Object.entries(KINDS).map(([k, v]) => (
              <Button key={k} type="button" size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}
                className={kind === k ? "bg-slate-800" : ""} data-testid={`meeting-kind-${k}`}>{v}</Button>
            ))}
          </div>
          <div><Label>Assunto</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Conselho de classe 1º trimestre" data-testid="meeting-title-input" /></div>
          <div><Label>Estudante (opcional — aparece na ficha do aluno)</Label><StudentPicker value={student} onChange={setStudent} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Depois</Button>
          <Button onClick={go} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="doc-generate-btn">
            {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Gerando com IA...</> : `Gerar ${DOC_TYPES[docType]}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const useRecorder = () => {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const rec = useRef(null), stream = useRef(null), parts = useRef([]), segs = useRef([]), timer = useRef(null), seg = useRef(null);
  const startSegment = () => {
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
    const r = new MediaRecorder(stream.current, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 32000 });
    parts.current = [];
    r.ondataavailable = (e) => e.data.size && parts.current.push(e.data);
    r.onstop = () => { if (parts.current.length) segs.current.push(new Blob(parts.current, { type: r.mimeType })); r.onstopped?.(); };
    r.start(1000); rec.current = r;
    seg.current = setTimeout(() => { r.onstopped = startSegment; r.stop(); }, SEGMENT_MS);
  };
  const start = async () => {
    stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    segs.current = []; setSecs(0); startSegment(); setState("rec");
    timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
  };
  const pause = () => { rec.current?.pause(); clearInterval(timer.current); setState("paused"); };
  const resume = () => { rec.current?.resume(); timer.current = setInterval(() => setSecs((s) => s + 1), 1000); setState("rec"); };
  const stop = () => new Promise((res) => {
    clearInterval(timer.current); clearTimeout(seg.current);
    const r = rec.current;
    const finish = () => { stream.current?.getTracks().forEach((t) => t.stop()); setState("idle"); res(segs.current); };
    if (!r || r.state === "inactive") { finish(); return; }
    r.onstopped = finish; r.stop();
  });
  const cancel = async () => { await stop(); segs.current = []; setSecs(0); };
  return { state, secs, start, pause, resume, stop, cancel };
};

export const MeetingRecorderButton = ({ scopeParams }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("");
  const [meeting, setMeeting] = useState(null);
  const [choose, setChoose] = useState(false);
  const file = useRef(null);
  const r = useRecorder();

  const process = async (blobs) => {
    if (!blobs.length) { toast.error("Nenhum áudio gravado"); return; }
    try {
      setPhase("Enviando áudio...");
      const { data: m } = await api.post("/meetings", { kind: "REUNIAO", school_id: scopeParams?.school_id });
      for (let i = 0; i < blobs.length; i++) {
        setPhase(`Enviando áudio (${i + 1}/${blobs.length})...`);
        const b = blobs[i];
        const ext = b.name ? b.name.split(".").pop() : (b.type.includes("mp4") ? "m4a" : "webm");
        const fd = new FormData(); fd.append("index", String(i)); fd.append("file", b, b.name || `parte-${i + 1}.${ext}`);
        await api.post(`/meetings/${m.id}/segments`, fd, { timeout: 300000 });
      }
      setPhase("Transcrevendo a gravação (Whisper)... pode levar alguns minutos.");
      await api.post(`/meetings/${m.id}/transcribe`);
      const done = await waitStatus(m.id, ["TRANSCRITO"]);
      setMeeting(done); setOpen(false); setChoose(true);
    } catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); } finally { setPhase(""); }
  };
  const startRec = async () => {
    try { await r.start(); } catch { toast.error("Não foi possível acessar o microfone. Verifique a permissão do navegador."); }
  };
  const stopRec = async () => process(await r.stop());
  const onFile = (e) => { const fs = [...e.target.files]; e.target.value = ""; if (fs.length) process(fs); };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="meeting-record-open-btn">
        <Mic className="mr-1.5 h-4 w-4" /> Gravar reunião / atendimento
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!o && r.state === "idle" && !phase) setOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="meeting-record-dialog">
          <DialogHeader><DialogTitle>Gravar reunião ou atendimento</DialogTitle></DialogHeader>
          {phase ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="meeting-processing">
              <Loader2 className="h-8 w-8 animate-spin text-blue-700" /><p className="text-sm text-slate-600">{phase}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className={`grid h-24 w-24 place-items-center rounded-full ${r.state === "rec" ? "animate-pulse bg-red-100" : "bg-slate-100"}`}>
                <Mic className={`h-10 w-10 ${r.state === "rec" ? "text-red-600" : "text-slate-400"}`} />
              </div>
              <div className="font-display text-3xl font-bold tabular-nums text-slate-900" data-testid="meeting-timer">{fmtTime(r.secs)}</div>
              <p className="text-center text-xs text-slate-500">{r.state === "idle" ? "Avise os participantes de que a conversa será gravada. Ao finalizar, a IA transcreve e você escolhe o documento." : r.state === "paused" ? "Gravação pausada" : "Gravando..."}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {r.state === "idle" && <Button onClick={startRec} className="bg-red-600 hover:bg-red-700" data-testid="meeting-rec-start"><Mic className="mr-1.5 h-4 w-4" />Iniciar gravação</Button>}
                {r.state === "rec" && <Button variant="outline" onClick={r.pause} data-testid="meeting-rec-pause"><Pause className="mr-1.5 h-4 w-4" />Pausar</Button>}
                {r.state === "paused" && <Button variant="outline" onClick={r.resume} data-testid="meeting-rec-resume"><Play className="mr-1.5 h-4 w-4" />Continuar</Button>}
                {r.state !== "idle" && <Button onClick={stopRec} className="bg-slate-800" data-testid="meeting-rec-stop"><Square className="mr-1.5 h-4 w-4" />Finalizar e transcrever</Button>}
                {r.state !== "idle" && <Button variant="ghost" onClick={r.cancel} className="text-red-600" data-testid="meeting-rec-cancel">Descartar</Button>}
              </div>
              {r.state === "idle" && (
                <>
                  <input ref={file} type="file" hidden accept="audio/*,.webm,.mp3,.m4a,.wav,.ogg" onChange={onFile} data-testid="meeting-audio-file" />
                  <button type="button" onClick={() => file.current?.click()} className="flex items-center gap-1.5 text-sm text-blue-700 hover:underline" data-testid="meeting-upload-btn">
                    <Upload className="h-4 w-4" />ou enviar um arquivo de áudio (até 24 MB)
                  </button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ChooseDocDialog meeting={meeting} open={choose} onClose={() => { setChoose(false); navigate(`/reunioes/${meeting.id}`); }}
        onDone={(m) => { setChoose(false); navigate(`/reunioes/${m.id}`); }} />
    </>
  );
};
